import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchCharacters } from '../api/characters';
import { C, FONT } from '../theme/tokens';
import CharacterSelect from './CharacterSelect';
import Layout from '../components/Layout';
import Dashboard from './Dashboard';
import Find from './Find';
import DataVault from './vault';
import Settings from './Settings';
import AdminPanel from './AdminPanel';
import Fittings from './Fittings';
import Handbook from './handbook';
import WhatsNew from './WhatsNew';
import SystemPage from './system/SystemPage';
import SystemOverview from './system/SystemOverview';
import SystemSites from './system/SystemSites';
import SystemKillboard from './system/SystemKillboard';
import SiteDetailPage from './sites/SiteDetailPage';
import { useAuth } from '../services/AuthContext';

/** Sits between the login gate (Start.tsx) and the app shell: decides
 * whether a character picker is needed. The backend always has *some*
 * active character set right after login (the one that just authenticated)
 * — this screen is about letting a multi-character account confirm/switch
 * before landing on the dashboard, not about there being no active
 * character at all. */
const Gate: React.FC = () => {
  const { me } = useAuth();
  const { data: characters, isLoading } = useQuery({ queryKey: ['characters'], queryFn: fetchCharacters });
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    if (characters && characters.length <= 1) setConfirmed(true);
  }, [characters]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography sx={{ fontFamily: FONT.mono, color: C.blue }}>LOADING ROSTER&hellip;</Typography>
      </Box>
    );
  }

  if (!confirmed && characters && characters.length > 1) {
    return <CharacterSelect characters={characters} onConfirmed={() => setConfirmed(true)} />;
  }

  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Dashboard />} />
        {/* Find = the catalogue, queried by what a system IS. Distinct from
            /intel/current (the live current system) and /system/:slug (one
            system's address): this is the only surface that answers a
            question about systems nobody has scanned yet. */}
        <Route path="/find" element={<Find />} />
        {/* /intel/current = the live page: the active character's current
            system, moving with every jump, like the dashboard. /intel forwards
            there. /system/:slug is a fixed address for ONE system — the link
            you paste in corp chat — and does not move. Both are member-only;
            a deep link survives login via Start.tsx's returnTo. */}
        <Route path="/intel" element={<Navigate to="/intel/current" replace />} />
        <Route path="/intel/current" element={<SystemPage live />}>
          <Route index element={<SystemOverview />} />
          <Route path="sites" element={<SystemSites />} />
          <Route path="sites/:className/:siteName" element={<SiteDetailPage />} />
          <Route path="killboard" element={<SystemKillboard />} />
        </Route>
        <Route path="/system/:slug" element={<SystemPage />}>
          <Route index element={<SystemOverview />} />
          <Route path="sites" element={<SystemSites />} />
          {/* Nested under the system so a site link keeps its system
              context and the Sites tab stays lit. */}
          <Route path="sites/:className/:siteName" element={<SiteDetailPage />} />
          <Route path="killboard" element={<SystemKillboard />} />
        </Route>
        <Route path="/vault" element={<DataVault />} />
        <Route path="/fittings" element={<Fittings />} />
        {/* Corp doctrine. The content itself is member-gated server-side
            (routes/handbook.ts), so this route is a wayfinding entry, not the
            access control. */}
        <Route path="/handbook" element={<Handbook />} />
        <Route path="/whats-new" element={<WhatsNew />} />
        <Route path="/settings" element={<Settings />} />
        {(me.isAdmin || me.isDirector) && <Route path="/admin" element={<AdminPanel />} />}
      </Routes>
    </Layout>
  );
};

export default Gate;
